// Editor de uma carta: mecânica (com o vocabulário do motor), efeitos, textos PT/EN e JSON avançado.
// Edita a definição existente campo a campo — a ordem das chaves mantém-se, para os ficheiros do jogo não "mexerem"
// mais do que a alteração feita. O backend valida tudo outra vez.
import { useApi } from "@ecliptica/api-client/react";
import { CardImage } from "@ecliptica/game-ui";
import { activeLangOr, contentLangs, useTranslation, type Lang } from "@ecliptica/i18n";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  Label,
  NativeSelect,
  SegmentedControl,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from "@ecliptica/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import { useCan } from "../../features/auth/RequireStaff";

type Def = Record<string, unknown> & { id: string };
type Effect = Record<string, unknown>;
type Texts = Record<string, Record<string, string>>;
type Vocab = {
  actions: string[];
  triggers: string[];
  conditions: string[];
  effectTargets: string[];
  sealOn: string[];
  types: string[];
  aspects: string[];
  rarities: string[];
  keywords: string[];
  tokens: string[];
  glyphs: string[];
};
type Problem = { path: string; code: string; detail?: string; where: string };

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm font-semibold">
      {label}
      {children}
    </label>
  );
}

function Select({ value, options, onChange, empty, disabled, label }: { value: unknown; options: string[]; onChange: (v: string) => void; empty?: string; disabled?: boolean; label?: (o: string) => string }) {
  return (
    <NativeSelect value={value === undefined || value === null ? "" : String(value)} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      {empty !== undefined && <option value="">{empty}</option>}
      {options.map((o) => (
        <option key={o} value={o}>
          {label ? label(o) : o}
        </option>
      ))}
    </NativeSelect>
  );
}

export function CardEditorPage() {
  const { id = "" } = useParams();
  const { t, i18n } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const can = useCan();
  const editable = can("content.edit");
  const [def, setDef] = useState<Def | null>(null);
  const [texts, setTexts] = useState<Texts>({});
  const [json, setJson] = useState("");
  const [jsonError, setJsonError] = useState(false);
  const [reason, setReason] = useState("");
  const [problems, setProblems] = useState<Problem[]>([]);
  // Textos e imagem: só as línguas ATIVAS. Os textos das inativas ficam em `texts` tal como vieram e voltam
  // intactos ao gravar (não se mostram nem se apagam).
  const langs = contentLangs();
  const [imgLang, setImgLang] = useState<Lang>(() => activeLangOr(i18n.language));

  const q = useQuery({
    queryKey: ["content-card", id],
    queryFn: async () => {
      const res = await api.v1.admin.content.cards[":id"].$get({ param: { id } });
      if (res.status === 404) return null;
      return (await res.json()) as unknown as { id: string; kind: string; editionCode: string; def: Def; texts: Texts; pending: { fields: string[]; texts: Record<string, string[]> } | null };
    },
  });
  const vocab = useQuery({ queryKey: ["content-vocab"], queryFn: async () => (await (await api.v1.admin.content.vocabulary.$get()).json()) as unknown as Vocab });

  useEffect(() => {
    if (!q.data) return;
    setDef(structuredClone(q.data.def));
    setTexts(structuredClone(q.data.texts));
    setJson(JSON.stringify(q.data.def, null, 2));
  }, [q.data]);

  if (q.isPending || !vocab.data) return <Skeleton className="h-64 w-full" />;
  if (!q.data || !def) return <EmptyState title={t("error.notFound")} />;
  const v = vocab.data;
  const isToken = q.data.kind === "token";

  /** Muda um campo da definição sem mexer na ordem das restantes chaves (chaves novas vão para o fim). */
  const set = (k: string, value: unknown) => {
    const next = { ...def } as Def;
    if (value === undefined || value === "") delete next[k];
    else next[k] = value;
    setDef(next);
    setJson(JSON.stringify(next, null, 2));
  };
  const effects = (Array.isArray(def.effects) ? def.effects : []) as Effect[];
  const setEffect = (i: number, k: string, value: unknown) => {
    const list = effects.map((e, j) => {
      if (j !== i) return e;
      const n = { ...e };
      if (value === undefined || value === "" || (typeof value === "number" && Number.isNaN(value))) delete n[k];
      else n[k] = value;
      return n;
    });
    set("effects", list);
  };
  const setText = (l: string, k: string, value: string) => setTexts({ ...texts, [l]: { ...texts[l], [k]: value } });
  const num = (s: string) => (s === "" ? undefined : Math.trunc(Number(s)));

  async function save() {
    setProblems([]);
    const res = await api.v1.admin.content.cards[":id"].$put({ param: { id }, json: { def: def ?? undefined, texts, reason: reason || undefined } });
    if (res.status === 400) {
      setProblems(((await res.json()) as { errors?: Problem[] }).errors ?? []);
      return;
    }
    if (!res.ok) return toast.error(t("error.generic"));
    toast.success(t("content.editor.saved"));
    setReason("");
    await qc.invalidateQueries({ queryKey: ["content-card", id] });
    await qc.invalidateQueries({ queryKey: ["content-cards"] });
  }

  const pendingList = q.data.pending ? [...q.data.pending.fields, ...Object.entries(q.data.pending.texts).map(([l, f]) => `${l}: ${f.join(", ")}`)].join("; ") : "";

  return (
    <div className="flex flex-col gap-4">
      <Link to="/cards" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> {t("content.editor.back")}
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-extrabold text-gold">{texts[imgLang]?.name ?? id}</h1>
        <Badge variant="secondary">{id}</Badge>
        {isToken && <Badge variant="outline">{t("content.token")}</Badge>}
      </div>
      {q.data.pending && <Alert variant="warning">{t("content.editor.pending", { fields: pendingList })}</Alert>}
      {!editable && <Alert>{t("content.editor.readOnly")}</Alert>}

      <div className="grid gap-4 xl:grid-cols-[18rem_1fr]">
        <Card className="self-start">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("content.editor.image")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {langs.length > 1 && <SegmentedControl label="lang" value={imgLang} onChange={setImgLang} options={langs.map((l) => ({ value: l, label: l.toUpperCase() }))} />}
            <CardImage id={id} lang={imgLang} alt={texts[imgLang]?.name ?? id} loading="eager" />
            <p className="text-xs text-muted-foreground">{t("content.editor.imageNote")}</p>
          </CardContent>
        </Card>

        <div className="flex min-w-0 flex-col gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t("content.editor.basics")}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <Row label={t("content.editor.type")}>
                <Select value={def.type} options={v.types} disabled={!editable} onChange={(x) => set("type", x)} label={(o) => t(`game.type.${o}`)} />
              </Row>
              <Row label={t("content.editor.aspect")}>
                <Select value={def.aspect} options={v.aspects} disabled={!editable} onChange={(x) => set("aspect", x)} label={(o) => t(`game.aspect.${o}`)} />
              </Row>
              {!isToken && (
                <Row label={t("content.editor.rarity")}>
                  <Select value={def.rarity} options={v.rarities} disabled={!editable} onChange={(x) => set("rarity", x)} label={(o) => t(`game.rarity.${o}`)} />
                </Row>
              )}
              <Row label={t("content.editor.glyph")}>
                <Select value={def.glyph} options={v.glyphs} disabled={!editable} onChange={(x) => set("glyph", x)} />
              </Row>
              <Row label={t("content.editor.cost")}>
                <Input type="number" min={0} max={10} disabled={!editable} value={String(def.cost ?? "")} onChange={(e) => set("cost", num(e.target.value))} />
              </Row>
              {def.type === "unit" && (
                <>
                  <Row label={t("content.editor.atk")}>
                    <Input type="number" min={0} max={20} disabled={!editable} value={String(def.atk ?? "")} onChange={(e) => set("atk", num(e.target.value))} />
                  </Row>
                  <Row label={t("content.editor.hp")}>
                    <Input type="number" min={1} max={20} disabled={!editable} value={String(def.hp ?? "")} onChange={(e) => set("hp", num(e.target.value))} />
                  </Row>
                </>
              )}
              {def.type === "seal" && (
                <Row label={t("content.editor.sealOn")}>
                  <Select value={def.sealOn} options={v.sealOn} disabled={!editable} onChange={(x) => set("sealOn", x)} />
                </Row>
              )}
              <div className="sm:col-span-3 lg:col-span-4">
                <p className="mb-1.5 text-sm font-semibold">{t("content.editor.keywords")}</p>
                <div className="flex flex-wrap gap-2">
                  {v.keywords.map((k) => {
                    const on = Array.isArray(def.keywords) && (def.keywords as string[]).includes(k);
                    return (
                      <button
                        key={k}
                        type="button"
                        disabled={!editable}
                        onClick={() => set("keywords", on ? (def.keywords as string[]).filter((x) => x !== k) : [...((def.keywords as string[]) ?? []), k])}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold ${on ? "border-gold bg-gold/20 text-gold-bright" : "text-muted-foreground"}`}
                      >
                        {k}
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">{t("content.editor.effects")}</CardTitle>
              {editable && (
                <Button size="sm" variant="outline" onClick={() => set("effects", [...effects, { on: "play", do: v.actions[0] }])}>
                  <Plus /> {t("content.editor.addEffect")}
                </Button>
              )}
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {effects.map((e, i) => (
                <div key={i} className="grid gap-2 rounded-md border p-3 sm:grid-cols-3 lg:grid-cols-6">
                  <Row label={t("content.editor.on")}>
                    <Select value={e.on} options={v.triggers} empty={t("content.editor.onPlay")} disabled={!editable} onChange={(x) => setEffect(i, "on", x)} />
                  </Row>
                  <Row label={t("content.editor.if")}>
                    <Select value={e.if} options={v.conditions} empty={t("content.editor.none")} disabled={!editable} onChange={(x) => setEffect(i, "if", x)} />
                  </Row>
                  <Row label={t("content.editor.do")}>
                    <Select value={e.do} options={v.actions} disabled={!editable} onChange={(x) => setEffect(i, "do", x)} />
                  </Row>
                  <Row label={t("content.editor.target")}>
                    <Select value={e.target} options={v.effectTargets} empty={t("content.editor.none")} disabled={!editable} onChange={(x) => setEffect(i, "target", x)} />
                  </Row>
                  <Row label={t("content.editor.amount")}>
                    <Input
                      disabled={!editable}
                      value={e.amount === undefined ? "" : String(e.amount)}
                      onChange={(ev) => setEffect(i, "amount", ev.target.value === "all" ? "all" : num(ev.target.value))}
                    />
                  </Row>
                  <div className="flex items-end gap-2">
                    {(e.do === "buff" || e.atk !== undefined || e.hp !== undefined) && (
                      <>
                        <Row label={t("content.editor.atk")}>
                          <Input type="number" disabled={!editable} value={e.atk === undefined ? "" : String(e.atk)} onChange={(ev) => setEffect(i, "atk", num(ev.target.value))} />
                        </Row>
                        <Row label={t("content.editor.hp")}>
                          <Input type="number" disabled={!editable} value={e.hp === undefined ? "" : String(e.hp)} onChange={(ev) => setEffect(i, "hp", num(ev.target.value))} />
                        </Row>
                      </>
                    )}
                  </div>
                  {(e.do === "addKeyword" || e.kw !== undefined) && (
                    <Row label={t("content.editor.kw")}>
                      <Select value={e.kw} options={v.keywords} empty={t("content.editor.none")} disabled={!editable} onChange={(x) => setEffect(i, "kw", x)} />
                    </Row>
                  )}
                  {(e.do === "summon" || e.token !== undefined) && (
                    <Row label={t("content.editor.tokenField")}>
                      <Select value={e.token} options={v.tokens} empty={t("content.editor.none")} disabled={!editable} onChange={(x) => setEffect(i, "token", x)} />
                    </Row>
                  )}
                  {editable && (
                    <div className="flex items-end">
                      <Button size="sm" variant="ghost" onClick={() => set("effects", effects.filter((_, j) => j !== i))}>
                        <Trash2 /> {t("content.editor.removeEffect")}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t("content.editor.texts")}</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue={langs[0]}>
                {langs.length > 1 && (
                  <TabsList>
                    {langs.map((l) => (
                      <TabsTrigger key={l} value={l}>
                        {l.toUpperCase()}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                )}
                {langs.map((l) => (
                  <TabsContent key={l} value={l} className="grid gap-3 sm:grid-cols-2">
                    <Field label={t("content.editor.name")} maxLength={60} disabled={!editable} value={texts[l]?.name ?? ""} onChange={(e) => setText(l, "name", e.target.value)} />
                    <Field label={t("content.editor.tribe")} maxLength={60} disabled={!editable} value={texts[l]?.tribe ?? ""} onChange={(e) => setText(l, "tribe", e.target.value)} />
                    <div className="flex flex-col gap-1.5 sm:col-span-2">
                      <Label htmlFor={`text-${l}`}>{t("content.editor.text")}</Label>
                      <Textarea id={`text-${l}`} maxLength={300} disabled={!editable} value={texts[l]?.text ?? ""} onChange={(e) => setText(l, "text", e.target.value)} />
                    </div>
                    <div className="flex flex-col gap-1.5 sm:col-span-2">
                      <Label htmlFor={`flavor-${l}`}>{t("content.editor.flavor")}</Label>
                      <Textarea id={`flavor-${l}`} maxLength={240} disabled={!editable} value={texts[l]?.flavor ?? ""} onChange={(e) => setText(l, "flavor", e.target.value)} className="min-h-16 italic" />
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>

          <details className="rounded-lg border p-4">
            <summary className="cursor-pointer text-sm font-semibold">{t("content.editor.advanced")}</summary>
            <p className="mt-2 text-xs text-muted-foreground">{t("content.editor.advancedHint")}</p>
            <Textarea
              className="mt-2 min-h-64 font-mono text-xs"
              disabled={!editable}
              value={json}
              onChange={(e) => {
                setJson(e.target.value);
                try {
                  const parsed = JSON.parse(e.target.value) as Def;
                  setDef({ ...parsed, id });
                  setJsonError(false);
                } catch {
                  setJsonError(true);
                }
              }}
            />
            {jsonError && <p className="mt-1 text-xs text-destructive">{t("content.editor.badJson")}</p>}
          </details>

          {problems.length > 0 && (
            <Alert variant="destructive">
              <p className="font-semibold">{t("content.editor.errors")}</p>
              <ul className="mt-1 list-disc pl-5">
                {problems.map((p, i) => (
                  <li key={i}>
                    <code>
                      {p.where}
                      {p.path ? `.${p.path}` : ""}
                    </code>
                    : {t(`content.errors.${p.code}`)}
                    {p.detail ? ` (${p.detail})` : ""}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {editable && (
            <div className="flex flex-wrap items-end gap-3">
              <Field label={t("content.editor.reason")} className="min-w-64 flex-1" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
              <Button variant="primary" disabled={jsonError} onClick={() => void save()}>
                {t("content.editor.save")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
